// @ts-check
import {Component} from 'panel';

import {registerMPElement} from '../../util/register-element';

import template from './index.jade';
import css from './index.styl?inline';

const VISIBILITY_HIDDEN = `hidden`;
const VISIBILITY_TRANSITIONING = `transitioning`;
const VISIBILITY_VISIBLE = `visible`;

const PLACEMENT = {
  TOP: `top`,
  RIGHT: `right`,
  BOTTOM: `bottom`,
  LEFT: `left`,
};

const DISPLAY_TYPE = {
  DEFAULT: `default`,
  ERROR: `error`,
  CHART: `chart`,
};

const MIN_BUFFER_FROM_EDGE = 4; // px;

/**
 * @typedef {Object} TooltipState
 * @property {string} visibility
 * @property {string} placement
 * @property {{left?: string, top?: string}} wrapperStyles
 * @property {{left?: string}} arrowStyleOverrides
 */

/** @extends {Component<TooltipState>} */
export default class Tooltip extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'always-show': `boolean`,
      offset: {type: `number`, default: 8},
      'max-width': `string`,
      placement: {type: `string`, default: PLACEMENT.TOP, enum: Object.values(PLACEMENT)},
      type: {type: `string`, default: DISPLAY_TYPE.DEFAULT, enum: Object.values(DISPLAY_TYPE)},
    };
  }

  constructor() {
    super();
    // track added listeners so we can remove them in disconnectedCallback and be GC friendly
    this._listeners = new Map();
  }

  static get PLACEMENTS() {
    return Object.values(PLACEMENT);
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      /** @type {TooltipState} */
      defaultState: {
        visibility: VISIBILITY_HIDDEN,
        placement: ``,
        wrapperStyles: {
          left: `0px`,
          top: `0px`,
        },
        arrowStyleOverrides: {},
      },
      helpers: {
        stopPropagation: (ev) => {
          ev.stopPropagation();
        },
        getTooltipEl: () => {
          return this.el.querySelector(`.mp-tooltip-main`);
        },
      },
    };
  }

  show() {
    this.update({
      visibility: VISIBILITY_TRANSITIONING,
    });

    this.cancelPendingPositionTooltipIfNeeded();
    this._pendingPositionTooltipRaf = window.requestAnimationFrame(() => {
      const tooltipEl = this.helpers.getTooltipEl();
      this._positionTooltip(tooltipEl);
      this.update({
        visibility: VISIBILITY_VISIBLE,
      });
    });
  }

  _positionTooltip(tooltipEl) {
    if (tooltipEl) {
      // Since computation of top/left offsets depend on tooltip's height/width, these offsets must be computed
      // after tooltip is visible, hence in requestAnimationFrame.

      let parentOffset;
      const parentPosition = window.getComputedStyle(this.parentEl).getPropertyValue(`position`);
      // The tooltip has position absolute, which means it will be positioned relative to the nearest ancestor with
      // non-static position. Different handling is needed depending on whether the direct parent of the tooltip
      // has non-static positioning.
      if (parentPosition === `static`) {
        const offsetBoundingRect = this.parentEl.offsetParent.getBoundingClientRect();
        const parentBoundingRect = this.parentEl.getBoundingClientRect();
        parentOffset = {
          left: parentBoundingRect.left - offsetBoundingRect.left,
          top: parentBoundingRect.top - offsetBoundingRect.top,
        };
      } else {
        parentOffset = {
          left: 0,
          top: 0,
        };
      }

      const wrapperStyles = {};
      const arrowStyleOverrides = {};

      const offset = this.attr(`offset`);
      const placement = this.attr(`placement`);

      if (placement === PLACEMENT.TOP || placement === PLACEMENT.BOTTOM) {
        /**                 viewport
         *  --------------------------------------------------
         *  |                                                |
         *  |       < t o o l t i p    c o n t e n t >       |
         *  |                       V                        |
         *  |                   < parent >                   |
         *  |       { overhang }          { overhang }       |
         *  |{ available space }          { available space }|
         *  |                                                |
         *  --------------------------------------------------
         */
        const parentBounds = this.parentEl.getBoundingClientRect();
        const viewportWidth = document.documentElement.clientWidth;
        const overhang = (tooltipEl.offsetWidth - this.parentEl.offsetWidth) / 2;
        const availableSpaceToTheLeft = parentBounds.x - MIN_BUFFER_FROM_EDGE;
        const availableSpaceToTheRight =
          window.scrollX + viewportWidth - (parentBounds.x + parentBounds.width) - MIN_BUFFER_FROM_EDGE;
        let xAxisAdjustment = 0;
        let overhangLeft = overhang;

        // If the tooltip will go outside of the right bound of the viewport, adjust it left so it won't
        if (overhang > availableSpaceToTheRight) {
          xAxisAdjustment = availableSpaceToTheRight - overhang;
          overhangLeft = overhang - xAxisAdjustment;
        }

        // Now, if the tooltip will go outside of the left bound of the viewport, adjust it right so it won't
        if (overhangLeft > availableSpaceToTheLeft) {
          xAxisAdjustment = overhang - availableSpaceToTheLeft;
        }

        wrapperStyles.left = `${parentOffset.left - overhang + xAxisAdjustment}px`;
        arrowStyleOverrides.left = `calc(50% - ${xAxisAdjustment}px)`;

        if (placement === PLACEMENT.TOP) {
          wrapperStyles.top = `${parentOffset.top - tooltipEl.offsetHeight - offset}px`;
        } else {
          wrapperStyles.top = `${parentOffset.top + this.parentEl.offsetHeight + offset}px`;
        }
      } else if (placement === PLACEMENT.LEFT || placement === PLACEMENT.RIGHT) {
        wrapperStyles.top = `${parentOffset.top + (this.parentEl.offsetHeight - tooltipEl.offsetHeight) / 2}px`;

        if (placement === PLACEMENT.LEFT) {
          wrapperStyles.left = `${parentOffset.left - tooltipEl.offsetWidth - offset}px`;
        } else {
          wrapperStyles.left = `${parentOffset.left + this.parentEl.offsetWidth + offset}px`;
        }
      }

      this.update({
        wrapperStyles,
        arrowStyleOverrides,
      });
    }
  }

  hide() {
    this.cancelPendingPositionTooltipIfNeeded();
    this.update({visibility: VISIBILITY_HIDDEN});
  }

  /**
   * Used for adding/removing event listeners
   */
  get parentEl() {
    return this._parentEl || this.parentNode;
  }

  set parentEl(parentEl) {
    this._parentEl = parentEl;
  }

  connectedCallback() {
    super.connectedCallback();

    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);

    this.render();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (this.initialized) {
      this.render();
    }
  }

  render() {
    this.removeListenersIfNeeded();
    if (this.attr(`always-show`)) {
      this.show();
    } else {
      if (this.state.visibility === VISIBILITY_VISIBLE) {
        this.show();
      }
      this._addEventListener(this.parentEl, `mouseenter`, this.show);
      this._addEventListener(this.parentEl, `mouseleave`, this.hide);
    }
  }

  _addEventListener(el, event, listener) {
    el.addEventListener(event, listener);
    if (!this._listeners.has(el)) {
      this._listeners.set(el, {[event]: listener});
    } else {
      this._listeners.get(el)[event] = listener;
    }
  }

  removeListenersIfNeeded() {
    for (const [el, listeners] of this._listeners.entries()) {
      for (const [event, listener] of Object.entries(listeners)) {
        el.removeEventListener(event, listener);
      }
    }
  }

  cancelPendingPositionTooltipIfNeeded() {
    if (this._pendingPositionTooltipRaf) {
      window.cancelAnimationFrame(this._pendingPositionTooltipRaf);
      this._pendingPositionTooltipRaf = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeListenersIfNeeded();
    this.cancelPendingPositionTooltipIfNeeded();
  }
}

registerMPElement(`mp-tooltip`, Tooltip);
